import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Grammaire du mouvement, partagée par toutes les surfaces qui s'ouvrent et se
 * ferment (modale, panneau glissant, menu, visionneuse, infobulle, message
 * éphémère).
 *
 * Deux principes s'appliquent partout :
 *
 * 1. **Une sortie, pas seulement une entrée.** Un élément qui apparaît en
 *    fondu puis disparaît d'un coup se lit comme un défaut d'affichage. La
 *    sortie est plus courte que l'entrée : on décide lentement, le système
 *    répond vite.
 * 2. **Des transitions, pas des `@keyframes`.** Une transition se laisse
 *    interrompre et repart de la valeur courante ; une keyframe recommence à
 *    zéro. Rouvrir un menu pendant sa fermeture doit repartir d'où il en est,
 *    pas sauter.
 */

/** Entrée : le temps de comprendre ce qui arrive. */
export const D_ENTER = 220;
/** Sortie : plus courte, l'utilisateur a déjà décidé. */
export const D_EXIT = 140;

/*
 * L'infobulle est plus rapide que les surfaces qui portent une décision : elle
 * accompagne un survol, elle ne l'interrompt pas.
 */
export const D_ENTER_TIP = 160;
export const D_EXIT_TIP = 100;

/**
 * Vrai quand le système demande à réduire les animations.
 *
 * Écouté en JS et pas seulement en CSS : les durées ci-dessous pilotent aussi
 * des `setTimeout` de démontage, qu'une règle CSS ne peut pas raccourcir. Sans
 * ça, une fenêtre fermée resterait montée 140 ms de plus sans rien afficher.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

export interface OverlayMotion {
  /** `false` à la première image puis `true` : c'est ce basculement qui anime. */
  visible: boolean;
  /** Joue la sortie, puis prévient l'appelant une fois l'animation finie. */
  requestClose: () => void;
  /** Durée à appliquer à la transition, selon le sens en cours. */
  duration: number;
}

/**
 * Anime l'ouverture et la fermeture d'une surface, sans rien demander à
 * l'appelant.
 *
 * La fermeture est interceptée plutôt que déduite d'une propriété `open` :
 * dans cette application, la plupart des modales sont montées par un parent
 * (`{editing && <Formulaire />}`), qui retire le nœud du DOM avant qu'une
 * animation de sortie ait pu se jouer. En passant par `requestClose`, la
 * surface joue sa sortie **puis** prévient le parent, qui la démonte ensuite.
 */
export function useOverlayMotion(
  onClose: () => void,
  durations?: { enter?: number; exit?: number },
): OverlayMotion {
  const reduced = usePrefersReducedMotion();
  const enter = reduced ? 0 : (durations?.enter ?? D_ENTER);
  const exit = reduced ? 0 : (durations?.exit ?? D_EXIT);

  const [visible, setVisible] = useState(false);
  /*
   * La durée vit dans un état, et change à un moment très précis.
   *
   * Chromium ne démarre pas de transition quand `transition-duration` et la
   * valeur animée changent dans le même rendu : la surface saute d'un état à
   * l'autre. Mesuré sur la modale — opacité à 1,00 pendant 50 ms, puis 0,01
   * d'un coup, alors que la règle et la valeur écrites étaient correctes.
   *
   * On bascule donc sur la durée de sortie **une fois l'entrée terminée**, à un
   * instant où rien ne bouge : la fermeture n'a plus alors qu'une seule chose à
   * changer, l'opacité. L'asymétrie entrée/sortie est préservée sans reposer
   * sur un changement simultané.
   */
  const [duration, setDuration] = useState(enter);
  const closing = useRef(false);
  // `onClose` change souvent d'identité (fonction fléchée dans le rendu du
  // parent) : le garder dans une référence évite de reprogrammer la sortie.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    // Deux images : la première monte le nœud dans son état fermé, la seconde
    // bascule vers l'état ouvert. Sans ce délai, le navigateur regroupe les
    // deux états et il n'y a aucune transition à interpoler.
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
    const bascule = window.setTimeout(() => setDuration(exit), enter + 60);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(bascule);
    };
  }, [enter, exit]);

  const requestClose = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    setVisible(false);
    // Une fermeture demandée avant la fin de l'entrée se joue encore à la durée
    // d'entrée : c'est plus lent, mais jamais cassé.
    window.setTimeout(() => onCloseRef.current(), Math.max(enter, exit) + 20);
  }, [enter, exit]);

  return { visible, requestClose, duration };
}
